import { useState } from "react";
import { Link } from "react-router-dom";
import { ShareNetwork as Share2, DownloadSimple as Download } from "@phosphor-icons/react";
import { toast } from "sonner";
import { mask } from '../lib/privacy';

const C = { bg: "#FAF7F0", ink: "#2A1E5C", ink2: "#5D5A6B", muted: "#8B7E6A", gold: "#F5CB5C", green: "#3E8E4E", line: "#E8E2D5" };
const money = (n) => mask(`$${Number(n).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`);
const dayLbl = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" });

function roundRect(x, cx, cy, w, h, r) {
  x.beginPath();
  if (x.roundRect) { x.roundRect(cx, cy, w, h, r); } else { x.rect(cx, cy, w, h); }
  x.closePath();
}

export function wrapLines(x, text, maxWidth, maxLines) {
  const words = text.split(/\s+/);
  const lines = [];
  let line = "";
  for (let i = 0; i < words.length; i++) {
    const t = line ? `${line} ${words[i]}` : words[i];
    if (x.measureText(t).width > maxWidth && line) {
      lines.push(line);
      line = words[i];
      if (lines.length === maxLines) {
        lines[maxLines - 1] = `${lines[maxLines - 1].replace(/[.,;:]?$/, "")}…”`;
        return lines;
      }
    } else {
      line = t;
    }
  }
  if (line) lines.push(line);
  return lines;
}

let _logoImg = null;
function loadLogo() {
  return new Promise((resolve) => {
    if (_logoImg) return resolve(_logoImg);
    const img = new Image();
    img.onload = () => { _logoImg = img; resolve(img); };
    img.onerror = () => resolve(null);
    img.src = "/images/paydayrelaylogo.png";
  });
}

function drawCard(s, hideAmounts, logo) {
  const W = 1080, H = 1350;
  const cv = document.createElement("canvas");
  cv.width = W; cv.height = H;
  const x = cv.getContext("2d");

  x.fillStyle = C.bg; x.fillRect(0, 0, W, H);
  x.fillStyle = C.gold; x.fillRect(0, 0, W, 16);
  x.textAlign = "center";

  if (logo) {
    const lh = 170;
    const lw = lh * (logo.width / logo.height);
    x.drawImage(logo, (W - lw) / 2, 34, lw, lh);
    x.fillStyle = C.muted; x.font = "bold 30px Arial";
    x.fillText("W E E K L Y   R E C A P", W / 2, 245);
  } else {
    x.fillStyle = C.muted; x.font = "bold 34px Arial";
    x.fillText("🏁 P A Y D A Y   R E L A Y", W / 2, 130);
    x.font = "30px Arial";
    x.fillText("WEEKLY RECAP", W / 2, 178);
  }

  x.fillStyle = C.ink; x.font = "bold 52px Georgia";
  const relayLabel = s.relay_number ? `Relay #${s.relay_number}` : "This week";
  x.fillText(`${relayLabel} · ${dayLbl(s.week_start)} – ${dayLbl(s.week_end)}`, W / 2, logo ? 320 : 280);

  const target = s.weekly_target || 0;
  const earned = s.earned || 0;
  const pct = target > 0 ? Math.min(Math.round((earned / target) * 100), 999) : 0;
  const cleared = s.status === "cleared" || s.status === "green";

  if (s.status === "green") {
    x.fillStyle = C.green; x.font = "bold 46px Arial";
    x.fillText("IN THE GREEN 🟢", W / 2, 470);
    x.font = "bold 170px Georgia";
    x.fillText(hideAmounts ? `${pct}%` : `+${money(s.surplus || 0)}`, W / 2, 650);
    x.fillStyle = C.ink2; x.font = "38px Arial";
    x.fillText(hideAmounts ? "of my weekly target, and counting" : "earned past my weekly target", W / 2, 720);
  } else if (s.status === "cleared") {
    x.fillStyle = C.ink; x.font = "bold 46px Arial";
    x.fillText("RELAY CLEARED 🏁", W / 2, 470);
    x.font = "bold 170px Georgia";
    x.fillText("100%", W / 2, 650);
    x.fillStyle = C.ink2; x.font = "38px Arial";
    x.fillText(hideAmounts ? "weekly target met" : `${money(target)} weekly target met`, W / 2, 720);
  } else {
    x.fillStyle = C.muted; x.font = "bold 46px Arial";
    x.fillText("STILL IN THE RACE", W / 2, 470);
    x.fillStyle = C.ink; x.font = "bold 170px Georgia";
    x.fillText(`${pct}%`, W / 2, 650);
    x.fillStyle = C.ink2; x.font = "38px Arial";
    x.fillText(hideAmounts ? "of my weekly target so far" : `${money(earned)} of my ${money(target)} weekly target`, W / 2, 720);
  }

  const bw = 720, bh = 40, bx = (W - bw) / 2, by = 790;
  x.fillStyle = C.line; roundRect(x, bx, by, bw, bh, 20); x.fill();
  const fw = Math.max(Math.min(pct, 100) / 100 * bw, cleared ? bw : 12);
  x.fillStyle = cleared ? C.green : C.gold; roundRect(x, bx, by, fw, bh, 20); x.fill();

  const quote = s.recap_quote && s.recap_quote.text ? s.recap_quote.text : null;
  let y = quote ? 920 : 940;
  if (s.streak && s.streak.cleared > 0) {
    x.fillStyle = C.ink; x.font = "bold 44px Arial";
    x.fillText(`🔥 ${s.streak.cleared} relay${s.streak.cleared === 1 ? "" : "s"} cleared this race`, W / 2, y);
    y += quote ? 68 : 80;
  }
  if (!hideAmounts && s.surplus_bank && s.surplus_bank.total > 0) {
    x.fillStyle = C.ink2; x.font = "40px Arial";
    x.fillText(`💰 ${money(s.surplus_bank.total)} bonus banked this race`, W / 2, y);
  }

  if (quote) {
    x.fillStyle = C.ink; x.font = "italic 36px Georgia";
    const lines = wrapLines(x, `“${quote}”`, 800, 3);
    let qy = 1155 - (lines.length - 1) * 48;
    for (const ln of lines) { x.fillText(ln, W / 2, qy); qy += 48; }
  }

  x.strokeStyle = C.line; x.lineWidth = 2;
  x.beginPath(); x.moveTo(200, 1185); x.lineTo(W - 200, 1185); x.stroke();
  x.fillStyle = C.muted; x.font = "34px Arial";
  x.fillText("Run your own race → paydayrelay.com", W / 2, 1252);

  return cv;
}

export const RecapShare = ({ summary }) => {
  const [hideAmounts, setHideAmounts] = useState(false);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const makePreview = async (hide) => {
    try {
      const logo = await loadLogo();
      setPreview(drawCard(summary, hide, logo).toDataURL("image/png"));
    } catch { toast.error("Couldn't draw the card, try again"); }
  };

  const toggleHide = (v) => {
    setHideAmounts(v);
    if (preview) makePreview(v);
  };

  const share = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const logo = await loadLogo();
      const cv = drawCard(summary, hideAmounts, logo);
      const blob = await new Promise((r) => cv.toBlob(r, "image/png"));
      const file = new File([blob], "payday-relay-week.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "My Payday Relay week" });
      } else {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = "payday-relay-week.png";
        a.click();
        toast.success("Recap card saved, send it to anyone cheering you on 🏁");
      }
    } catch (e) {
      if (e && e.name !== "AbortError") toast.error("Couldn't share, try the download instead");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl p-5 lg:p-6 pr-card pr-light" data-testid="caddy-recap-card">
      <p className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: C.muted }}>Share your week</p>
      <p className="text-[13px] leading-relaxed mb-2" style={{ color: C.ink2 }}>
        Turn this week into a win card, send it to a partner, parent, or friend who's rooting for your race.
      </p>
      <p className="text-[11px] italic mb-3" style={{ color: C.muted }} data-testid="recap-quote-note">
        {summary.recap_quote?.text ? (
          <>⭐ Includes your journal quote, swap it anytime in <Link to="/app/journal" className="underline">your journal</Link>.</>
        ) : (
          <>Star a reflection in <Link to="/app/journal" className="underline">your journal</Link> to feature it on the card.</>
        )}
      </p>
      {preview && (
        <img src={preview} alt="Weekly recap card preview" className="w-full rounded-xl border mb-3" style={{ borderColor: C.line }} data-testid="recap-preview" />
      )}
      <label className="flex items-center gap-2 text-xs mb-3 cursor-pointer" style={{ color: C.ink2 }}>
        <input
          type="checkbox"
          checked={hideAmounts}
          onChange={(e) => toggleHide(e.target.checked)}
          className="w-4 h-4"
          data-testid="recap-hide-amounts"
        />
        Hide dollar amounts (show percentages only)
      </label>
      <div className="flex gap-2">
        {!preview ? (
          <button
            type="button"
            onClick={() => makePreview(hideAmounts)}
            className="flex-1 py-3 rounded-full font-bold text-sm inline-flex items-center justify-center gap-1.5 transition-transform hover:scale-[1.02]"
            style={{ background: C.gold, color: C.ink }}
            data-testid="recap-create-btn"
          >
            📸 Create my recap card
          </button>
        ) : (
          <button
            type="button"
            onClick={share}
            disabled={busy}
            className="flex-1 py-3 rounded-full font-bold text-sm inline-flex items-center justify-center gap-1.5 disabled:opacity-40 transition-transform hover:scale-[1.02]"
            style={{ background: C.ink, color: "#fff" }}
            data-testid="recap-share-btn"
          >
            {navigator.canShare ? <Share2 className="w-4 h-4" /> : <Download className="w-4 h-4" />}
            {busy ? "Working…" : navigator.canShare ? "Share it" : "Download it"}
          </button>
        )}
      </div>
    </div>
  );
};
